// contextMentions — pure diff over PowerComposer's mention list.
//
// PowerComposer is a CONTROLLED component: every pick or removal reports
// the FULL next `mentions` array (`commitMention` pushes `[...mentions,
// candidate]`; the chip's own × filters one out). The host (AppShellHost)
// needs to know WHICH item just appeared (to dispatch `introduceToContext`,
// context spine step 3a) and which just disappeared (to dispatch
// `dropFromContext`) without re-deriving that inline in a big component —
// a pure diff, unit-testable without mounting React.

export {
    addedMentions,
    removedMentions,
    withMentionPicked,
    withMentionRemoved,
};

import type { ComposerResourceRef } from "../components/Composer/PowerComposer/types.js";

const addedMentions = (
    prev: readonly ComposerResourceRef[],
    next: readonly ComposerResourceRef[],
): readonly ComposerResourceRef[] =>
    next.filter((n) => !prev.some((p) => p.id === n.id));

const removedMentions = (
    prev: readonly ComposerResourceRef[],
    next: readonly ComposerResourceRef[],
): readonly ComposerResourceRef[] =>
    prev.filter((p) => !next.some((n) => n.id === p.id));

// The ContextCard's `onPick`/`onRemove` and PowerComposer's `@`-mention commit
// both funnel to the SAME controlled `mentions` list (one command, two
// entrances) — these are the pure forward-direction diffs that funnel does,
// the mirror of addedMentions/removedMentions (which the host uses to derive
// the introduce/drop dispatch). Picking an already-present source is a no-op,
// not a duplicate chip — the card's own menu disables that row, and this is the
// belt-and-braces at the state layer.
const withMentionPicked = (
    mentions: readonly ComposerResourceRef[],
    source: ComposerResourceRef,
): readonly ComposerResourceRef[] =>
    mentions.some((m) => m.id === source.id) ? mentions : [...mentions, source];

const withMentionRemoved = (
    mentions: readonly ComposerResourceRef[],
    id: string,
): readonly ComposerResourceRef[] => mentions.filter((m) => m.id !== id);
